import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { isDefined } from '../../utilities/is-defined';
import { CONFIRMATION_TYPES } from '../../enums/confirmation-types.enum';
import { TIMESHEET_KEYS } from '../../enums/timesheet.enum';
import { ConcurrentStorageData } from '../../types/concurrent-usage';
import { NotificationCompletedListModel } from '../../models/low-level/notification-completed.model';
import { isNgRxEnabled } from '../../state/notification/notification-updates';
import {
    CONFIRMATION_CONCURRENT_STORE_KEY,
    isStoredLegacyConfirmation,
    legacyConfirmationTypeFromKey,
    StoredLegacyConfirmation,
} from '../../state/confirmation/utility/from-json-confirmation-model';
import type { StoredLegacyTimesheet } from '../../state/timesheet/utils/from-json-timesheet-model';
import { ConcurrentUsageService } from '../concurrent-usage/concurrent-usage.service';
import { KEY_NOTIFICATION_COMPLETED } from '../notification-status/storage-notification-status.service';

/** A legacy concurrency-store key that held data, parsed. */
export type LegacyRead<T> = Readonly<{ key: string; data: T }>;

/** What {@link LegacyDataMigrationService.readLegacyTimesheets} found. */
export type LegacyTimesheetsRead = Readonly<{
    current: LegacyRead<StoredLegacyTimesheet> | undefined;
    submitted: LegacyRead<StoredLegacyTimesheet> | undefined;
}>;

/**
 * One legacy confirmation, parsed: the type its key names, and the time its row was written — which
 * the legacy load preferred over the `saveTimestamp` inside the value, so the migration does too.
 */
export type LegacyConfirmationRead = LegacyRead<StoredLegacyConfirmation> &
    Readonly<{ confirmationType: CONFIRMATION_TYPES; timestamp: number }>;

/**
 * Reads and removes the data the legacy (non-NgRx) services left in the concurrency store, for the
 * one-way migration into the NgRx slices: the two timesheets `StorageTimesheetService` wrote, the
 * recently-completed list `StorageNotificationStatusService` wrote, and the one key per started
 * confirmation `StorageConfirmationService` wrote.
 *
 * "Legacy data exists" means a key holds a non-empty value that parses. A delete in this store never
 * removes the row: locally it only marks it deleted (`ConcurrentUsageDbService.remove`), and remotely
 * it overwrites the value with `''` because the gateway cannot delete. `readDataForKey` already
 * reports both as nothing — so a migrated key stays migrated, however it was deleted.
 *
 * Everything here is inert unless `enableNgRx` is on. The NgRx slices and their effects are
 * registered in every configuration, and their hydration also runs on the sync that the legacy path
 * triggers, so without this gate a device on the legacy path would migrate — and then delete — the
 * data it is still working with.
 *
 * A key whose value does not parse is reported as absent and left in place, so nothing that could
 * not be read is ever deleted.
 */
@Injectable({ providedIn: 'root' })
export class LegacyDataMigrationService {
    // Read once, in the injection context `isNgRxEnabled` needs.
    private readonly enabled = isNgRxEnabled();

    public constructor(
        private readonly concurrentUsageService: ConcurrentUsageService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {}

    /** The legacy `currentTimesheet` and `submittedTimesheet`, each `undefined` when it holds nothing. */
    public readLegacyTimesheets(): Observable<LegacyTimesheetsRead> {
        return forkJoin({
            current: this.readKey<StoredLegacyTimesheet>(TIMESHEET_KEYS.CURRENT_TIMESHEET),
            submitted: this.readKey<StoredLegacyTimesheet>(TIMESHEET_KEYS.SUBMITTED_TIMESHEET),
        });
    }

    /** The legacy recently-completed list, `undefined` when it holds nothing. */
    public readLegacyCompletions(): Observable<LegacyRead<NotificationCompletedListModel> | undefined> {
        return this.readKey<NotificationCompletedListModel>(KEY_NOTIFICATION_COMPLETED);
    }

    /**
     * Every started confirmation the legacy `StorageConfirmationService` left, one per
     * `CONFIRMATION-<notification>-<type>` key. Unlike the other keys these cannot be read by name, so
     * they are found the way the legacy purge found them: among the present local entries. A remote
     * delete leaves an empty value behind, which reads as nothing like everywhere else here.
     *
     * Each key stands on its own: one whose value does not parse, or parses to something that is not a
     * confirmation, is logged and left in place without holding up the others.
     */
    public readLegacyConfirmations(): Observable<LegacyConfirmationRead[]> {
        if (!this.enabled) {
            return of([]);
        }
        return this.concurrentUsageService.getAllPresentEntries().pipe(
            map((entries) =>
                entries
                    .filter((entry) => entry.key.startsWith(`${CONFIRMATION_CONCURRENT_STORE_KEY}-`) && entry.value)
                    .map((entry) => this.parseLegacyConfirmation(entry))
                    .filter(isDefined),
            ),
            catchError((error) => {
                this.log.error(`LegacyDataMigrationService: the legacy confirmations could not be read: ${error}`);
                return of([]);
            }),
        );
    }

    /**
     * Soft-deletes the given legacy keys. Called only once what they held has been persisted in the
     * NgRx slice, so a failed delete is logged rather than propagated: the next hydration reads the
     * key again, and the idempotent merge makes the repeat harmless.
     *
     * @param keys the keys the migration read
     */
    public deleteLegacyKeys(keys: readonly string[]): Observable<void> {
        if (keys.length === 0) {
            return of(undefined);
        }
        return forkJoin(
            keys.map((key) =>
                this.concurrentUsageService.deleteDataForKey(key).pipe(
                    catchError((error) => {
                        this.log.error(`LegacyDataMigrationService: deleteLegacyKeys: "${key}": ${error}`);
                        return of(undefined);
                    }),
                ),
            ),
        ).pipe(
            map(() => {
                this.log.debug(`LegacyDataMigrationService: deleteLegacyKeys: deleted ${keys.join(', ')}`);
                return undefined;
            }),
        );
    }

    private parseLegacyConfirmation(entry: ConcurrentStorageData): LegacyConfirmationRead | undefined {
        const confirmationType = legacyConfirmationTypeFromKey(entry.key);
        try {
            const data = JSON.parse(entry.value) as Partial<StoredLegacyConfirmation> | null;
            if (
                isDefined(confirmationType) &&
                data !== null &&
                typeof data === 'object' &&
                isStoredLegacyConfirmation(data)
            ) {
                return { key: entry.key, data, confirmationType, timestamp: entry.timestamp };
            }
        } catch (error) {
            this.log.error(
                `LegacyDataMigrationService: "${entry.key}" could not be parsed, leaving it in place: ${error}`,
            );
            return undefined;
        }
        this.log.error(`LegacyDataMigrationService: "${entry.key}" is not a confirmation, leaving it in place`);
        return undefined;
    }

    private readKey<T>(key: string): Observable<LegacyRead<T> | undefined> {
        if (!this.enabled) {
            return of(undefined);
        }
        return this.concurrentUsageService.readDataForKey(key).pipe(
            map(([entry]) => {
                if (!entry?.value) {
                    return undefined;
                }
                const data = JSON.parse(entry.value) as T | null;
                // `JSON.stringify(undefined)` stored as a string, or a stored `null`, holds nothing.
                return data === null || typeof data !== 'object' ? undefined : { key, data };
            }),
            catchError((error) => {
                this.log.error(`LegacyDataMigrationService: "${key}" could not be read, leaving it in place: ${error}`);
                return of(undefined);
            }),
        );
    }
}
